import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { useDataProvider, useNotify } from "react-admin";

const PAYMENT_METHODS = {
  CASH: "Efectivo",
  CARD: "Tarjeta",
  QR: "QR",
  TIGO_MONEY: "Tigo Money",
  COURTESY: "Cortesia",
};

const TRANSACTION_TYPES = {
  INCOME: "Ingreso",
  EXPENSE: "Egreso",
};

const TransactionEditDialog = ({ open, handleClose, transaction, onSuccess, onDelete }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [formData, setFormData] = useState({
    type: "",
    amount: "",
    quantity: "",
    description: "",
    date: "",
    paymentMethod: "Efectivo",
  });

  useEffect(() => {
    if (transaction) {
      setFormData({
        type: transaction.type || "",
        amount: transaction.amount || "",
        quantity: transaction.quantity || "",
        description: transaction.description || "",
        date: transaction.date ? new Date(transaction.date).toISOString().split("T")[0] : "",
        paymentMethod: transaction.paymentMethod || "Efectivo",
      });
    }
  }, [transaction]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    try {
      const updateData = {
        ...formData,
        amount: parseFloat(formData.amount),
        quantity: parseInt(formData.quantity) || 0,
        date: new Date(formData.date + "T04:00:00.000Z").toISOString(),
      };

      await dataProvider.update("transaction", {
        id: transaction._id,
        data: updateData,
      });

      notify("Transacción actualizada con éxito", { type: "success" });
      handleClose();
      onSuccess();
    } catch (error) {
      notify("Error al actualizar la transacción", { type: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      await dataProvider.delete("transaction", { id: transaction._id });
      notify("Transacción eliminada con éxito", { type: "success" });
      handleClose();
      setConfirmDeleteOpen(false);
      onDelete();
    } catch (error) {
      notify("Error al eliminar la transacción", { type: "error" });
    }
  };

  if (!transaction) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Editar Transacción
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de transacción"
                  value={formData.type}
                  onChange={handleChange("type")}
                  required
                >
                  {Object.entries(TRANSACTION_TYPES).map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monto"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange("amount")}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cantidad"
                  type="number"
                  value={formData.quantity}
                  onChange={handleChange("quantity")}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Método de pago"
                  value={formData.paymentMethod}
                  onChange={handleChange("paymentMethod")}
                  required
                >
                  {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fecha"
                  type="date"
                  value={formData.date}
                  onChange={handleChange("date")}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleChange("description")}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={() => setConfirmDeleteOpen(true)} color="error" variant="outlined">
            Eliminar
          </Button>
          <Button onClick={handleSave} variant="contained">
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="confirm-delete-title"
      >
        <DialogTitle id="confirm-delete-title">Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar esta transacción?
            <br />
            <br />
            <strong>Tipo:</strong> {transaction?.type}
            <br />
            <strong>Monto:</strong> Bs. {transaction?.amount}
            <br />
            <strong>Descripción:</strong> {transaction?.description || "Sin descripción"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransactionEditDialog;
